import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTenant } from '@/contexts/TenantProvider'
import type { Database } from '@/types/database.types'

type RetailOrderRow = Database['public']['Tables']['retail_orders']['Row']
type RetailOrderInsert = Database['public']['Tables']['retail_orders']['Insert']

export type RetailOrderStatus =
  | 'pending_batch'
  | 'submitted'
  | 'acknowledged'
  | 'shipped'
  | 'delivered'
  | 'failed'

export const STATUS_LABELS: Record<RetailOrderStatus, string> = {
  pending_batch: 'Pending',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  shipped: 'Shipped',
  delivered: 'Delivered',
  failed: 'Failed',
}

export const STATUS_COLORS: Record<RetailOrderStatus, string> = {
  pending_batch: '#fbbf24',
  submitted: '#60a5fa',
  acknowledged: '#a78bfa',
  shipped: '#34d399',
  delivered: '#10b981',
  failed: '#ef4444',
}

/**
 * CRUD hook for retail_orders.
 * Scoped to current tenant via TenantProvider.
 *
 * @see PERD v10.0 §7.4 — Inventory-Free Retail Drop-shipping
 */
export function useRetailOrders(statusFilter?: RetailOrderStatus) {
  const { tenant } = useTenant()
  const queryClient = useQueryClient()
  const tenantId = tenant?.id

  const queryKey = useMemo(
    () => ['retail-orders', tenantId, statusFilter].filter(Boolean),
    [tenantId, statusFilter]
  )

  const {
    data: orders = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('retail_orders')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (statusFilter) {
        query = query.eq('edi_status', statusFilter)
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as RetailOrderRow[]
    },
    enabled: !!tenantId,
  })

  const createOrder = useMutation({
    mutationFn: async (input: Omit<RetailOrderInsert, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('retail_orders')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()
      if (error) throw error
      return data as RetailOrderRow
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const updateStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      trackingNumber,
    }: {
      id: string
      status: RetailOrderStatus
      trackingNumber?: string
    }) => {
      const updates: Record<string, unknown> = { edi_status: status }
      if (trackingNumber) updates.tracking_number = trackingNumber
      const { data, error } = await supabase
        .from('retail_orders')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data as RetailOrderRow
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  return { orders, isLoading, error, createOrder, updateStatus }
}
